import React from 'react';
import { Shield, Zap, Users, Target, Clock, Award } from 'lucide-react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const reasons = [
  {
    id: 'outcomes',
    icon: Target,
    title: 'Outcome-Obsessed',
    description: 'We don\'t bill for hours — we deliver results. Every engagement is tied to measurable business outcomes: revenue growth, cost savings, or efficiency gains.',
  },
  {
    id: 'speed',
    icon: Zap,
    title: 'Speed to Value',
    description: 'Rapid development cycles that get you to market fast. Our iterative approach means you start seeing value while competitors are still planning.',
  },
  {
    id: 'senior',
    icon: Users,
    title: 'Senior-Only Teams',
    description: 'No juniors learning on your project. Every team member has deep experience building high-stakes systems for growth-stage companies.',
  },
  {
    id: 'scale',
    icon: Shield,
    title: 'Built for Scale',
    description: 'Our architectures handle exponential growth without rewrites. We build systems designed to scale without technical debt slowing you down.',
  },
  {
    id: 'partnership',
    icon: Clock,
    title: 'Long-Term Partnership',
    description: 'We\'re not a vendor that disappears after delivery. We operate as an extension of your team — accountable for outcomes, invested in your success.',
  },
  {
    id: 'track-record',
    icon: Award,
    title: 'Enterprise-Grade Quality',
    description: 'Architecture designed for growth. Enterprise-grade security. High-availability SLAs. Systems that grow with your business.',
  },
];

export const WhyChooseSection = () => {
  const sectionRef = useScrollReveal();

  return (
    <section
      id="why-choose"
      ref={sectionRef}
      data-testid="why-choose-section"
      className="relative"
      style={{ backgroundColor: '#FFFFFF' }}
    >
      <div className="kv-container">
        <div className="pt-10 pb-12 lg:pt-12 lg:pb-14">
          {/* Section Header */}
          <div className="kv-reveal text-center max-w-3xl mx-auto mb-6">
            {/* Section Label */}
            <p
              className="text-xs font-semibold uppercase tracking-[0.2em] mb-3"
              style={{ color: '#0A3D91' }}
              data-testid="why-choose-label"
            >
              Why KritvaSoft
            </p>

            {/* Section Headline */}
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
              style={{ color: '#131B2E', letterSpacing: '-0.025em' }}
              data-testid="why-choose-headline"
            >
              Why High-Growth Companies Choose KritvaSoft
            </h2>

            {/* Section Description */}
            <p
              className="text-lg"
              style={{ color: '#475569', lineHeight: '1.7' }}
            >
              We're not for everyone. We work with ambitious companies ready to invest 
              $5K–$50K+ in scalable systems built for long-term growth.
            </p>
          </div>

          {/* Reasons Grid */}
          <div 
            className="kv-reveal-stagger grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            data-testid="why-choose-grid"
          >
            {reasons.map((reason) => (
              <div
                key={reason.id}
                className="kv-reveal group p-6 rounded-xl border"
                style={{ 
                  borderColor: '#E2E8F0',
                  backgroundColor: '#FFFFFF',
                  boxShadow: 'var(--kv-shadow-sm)',
                  transition: 'box-shadow 0.3s cubic-bezier(0.25,0.1,0.25,1), border-color 0.3s cubic-bezier(0.25,0.1,0.25,1), transform 0.3s cubic-bezier(0.25,0.1,0.25,1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--kv-shadow-lg)';
                  e.currentTarget.style.borderColor = '#CBD5E1';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--kv-shadow-sm)';
                  e.currentTarget.style.borderColor = '#E2E8F0';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                data-testid={`why-choose-${reason.id}`}
              >
                {/* Icon */}
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center mb-4 transition-colors duration-300"
                  style={{ backgroundColor: 'rgba(10, 61, 145, 0.05)' }}
                >
                  <reason.icon
                    className="w-5 h-5"
                    style={{ color: '#0A3D91' }}
                  />
                </div>

                {/* Title */}
                <h3
                  className="text-lg font-bold mb-2"
                  style={{ color: '#131B2E' }}
                >
                  {reason.title}
                </h3>

                {/* Description */}
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: '#475569' }}
                >
                  {reason.description}
                </p>
              </div>
            ))}
          </div>

          {/* Bottom Stats */}
          <div 
            className="mt-6 pt-4 border-t"
            style={{ borderColor: '#E2E8F0' }}
          >
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
              {[
                { value: '100x', label: 'Growth-Ready Architecture' },
                { value: '99.9%', label: 'Uptime SLA' },
                { value: 'Fast', label: 'MVP to Market' },
                { value: '24/7', label: 'Monitoring & Support' },
              ].map((stat, index) => (
                <div key={index} data-testid={`why-choose-stat-${index}`}>
                  <p
                    className="text-2xl lg:text-3xl font-bold mb-1"
                    style={{ color: '#0A3D91' }}
                  >
                    {stat.value}
                  </p>
                  <p
                    className="text-xs"
                    style={{ color: '#475569' }}
                  >
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
